import { lazy, Suspense, useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { storeClientId, getStoredClientId } from "../utils/tokenUtils";
import { useCan, useSettingsPageAccess } from "../hooks/useCan";
import { Loader2 } from "lucide-react";
import CustomizationPage from "./CustomizationPage";

// Lazy load all settings components
const TemplatesTab = lazy(() => import("../components/settings/TemplatesTab"));
const FieldsTab = lazy(() => import("../components/settings/FieldsTab"));
const UsersTab = lazy(() => import("../components/settings/UsersTab"));
const RolesTab = lazy(() => import("../components/settings/RolesTab"));
const ProjectsMembersTab = lazy(() => import("../components/settings/ProjectsMembersTab"));
const AuditLogsTab = lazy(() => import("../components/settings/AuditLogsTab"));
const Webhook = lazy(() => import("../components/settings/Webhook"));
const SecurityTab = lazy(() => import("../components/settings/SecurityTab"));
const SSOConfigurationTab = lazy(() =>
  import("../components/settings/SSOConfigurationTab"),
);
const IntegrationsTab = lazy(() => import("../components/settings/IntegrationsTab"));
const NetworkTab = lazy(() => import("../components/settings/NetworkTab"));
const ClientCreationWizard = lazy(() => import("../components/ClientCreationWizard"));
const ApiTab = lazy(() => import("../components/settings/ApiTab"));
const APIKey = lazy(() => import("../components/settings/APIKey"));
const AlertsTab = lazy(() => import("../components/settings/AlertsTab"));
const NotificationsTab = lazy(() => import("../components/settings/NotificationsTab"));
const BillsAndPermission = lazy(() => import("../components/settings/BillsAndPermission"));
const Trash = lazy(() => import("../components/settings/Trash"));

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(null)
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);

  // Get client_id from URL params (for chief-admin) or user's assigned client
  const urlClientId = searchParams.get("client_id");
  const storedClientId = getStoredClientId();
  const effectiveClientId = urlClientId || storedClientId || user?.client_id;
  /** Opened from All Clients → Edit: do not persist client in localStorage; return to /clients on save/cancel */
  const isEditFromAllClients = searchParams.get("from") === "all-clients";

  const userRole = user?.role_name || user?.role;
  const normalizeRole = (role) =>
    role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
  const normalizedUserRole = normalizeRole(userRole);
  const isUserChiefAdmin = normalizedUserRole === "chief-admin";
  const allowedRoles = [
    "chief-admin",
    "admin",
    "project-manager",
    "project_manager",
    "test-manager",
    "test_manager",
  ];
  const { canAccess: canAccessSettings, loading: settingsAccessLoading } =
    useSettingsPageAccess();

  const hasAllowedRole = allowedRoles.includes(normalizedUserRole);
  const { can: canViewCustomization } = useCan("customization", "view");
  const { can: templateAccess } = useCan("template", "view");
  const canAccessCustomizationPage = hasAllowedRole || canViewCustomization || templateAccess;

  // Always call hooks (React rules) — per-tab read/write via RBAC (chief-admin bypasses in useCan)
  const canViewUsersHook = useCan("user", "view");
  const canManageUsersHook = useCan("user", "add_edit");
  const canManageRolesHook = useCan("role_permission_config", "write");
  const canViewAuditHook = useCan("audit_logs", "read");
  const canViewWebhookHook = useCan("webhook", "read");
  const canViewConfigurationHook = useCan("configuration", "view");
  const canEditConfigurationHook = useCan("configuration", "add_edit");
  const canViewTemplateHook = useCan("template", "view");
  const canViewAPi = useCan("apiKey", "view");
  const canViewIntegrationHook = useCan("integration_management", "view");
  const canEditIntegrationHook = useCan("integration_management", "add_edit");

  const canViewUsers = isUserChiefAdmin || canViewUsersHook.can;
  const canManageUsers = isUserChiefAdmin || canManageUsersHook.can;
  const canManageRoles = isUserChiefAdmin || canManageRolesHook.can;
  const canViewAudit = isUserChiefAdmin || canViewAuditHook.can;
  const canViewWebhook = isUserChiefAdmin || canViewWebhookHook.can;
  const canViewConfiguration = isUserChiefAdmin || canViewConfigurationHook.can;
  const canEditConfiguration = isUserChiefAdmin || canEditConfigurationHook.can;
  const canViewTemplate = isUserChiefAdmin || canViewTemplateHook.can;
  const canViewOrManageIntegration =
    isUserChiefAdmin ||
    canViewIntegrationHook.can ||
    canEditIntegrationHook.can ||
    canManageRoles;

  // Define all sections (icons are handled in Sidebar.jsx)
  const currSection = searchParams.get("section");

  const sections = useMemo(() => {
    const data = [
      {
        id: "details",
        label: "Client Details",
        component: ClientCreationWizard,
        requiresPermission: canViewConfiguration,
        props: { viewMode: true },
      },
      {
        id: "users",
        label: "Users",
        component: UsersTab,
        requiresPermission: canViewUsers,
      },
      {
        id: "roles",
        label: "Roles",
        component: RolesTab,
        requiresPermission: canManageRoles,
      },
      {
        id: "projects",
        general: true,
        label: "Projects & Memberships",
        component: ProjectsMembersTab,
        requiresPermission: canManageUsers,
      },
      {
        id: "audit",
        label: "Audit Logs",
        component: AuditLogsTab,
        requiresPermission: canViewAudit,
      },
      {
        id: "webhook",
        general: true,

        label: "Webhook",
        component: Webhook,
        requiresPermission: canViewWebhook,
      },
      {
        id: "api_key",
        label: "API Key",
        general: true,

        component: APIKey,
        requiresPermission: canViewAPi,
      },
      {
        id: "security",
        label: "Security & SSO",
        general: true,

        component: SecurityTab,
        requiresPermission: canManageRoles,
      },
      {
        id: "sso_configuration",
        label: "SSO Configuration",
        general: true,
        component: SSOConfigurationTab,
        requiresPermission: canManageRoles,
      },
      {
        id: "integrations",
        label: "Integrations",
        general: true,

        component: IntegrationsTab,
        requiresPermission: canViewOrManageIntegration,
      },
      {
        id: "network",
        label: "Network / IP",
        general: true,

        component: NetworkTab,
        requiresPermission: canManageRoles,
      },
      {
        id: "api",
        label: "API & Automation",
        general: true,
        component: ApiTab,
        requiresPermission: canManageRoles,
      },
      {
        id: "customization",
        label: "Customization",
        general: true,

        component: CustomizationPage,
        requiresPermission: canAccessCustomizationPage,
      },
      {
        id: "alerts",
        label: "Alerts",
        general: true,

        component: AlertsTab,
        requiresPermission: canViewConfiguration,
      },
      {
        id: "notifications",
        label: "Notifications",
        general: true,

        component: NotificationsTab,
        requiresPermission: canViewConfiguration,
      },
      {
        id: "bills_and_payments",
        label: "Bills & Payments",
        general: true,
        component: BillsAndPermission,
        requiresPermission: canViewConfiguration,
      },
      {
        id: "trash",
        label: "Trash",
        component: Trash,
        requiresPermission: canViewConfiguration,
      },
      {
        id: "templates",
        label: "Test Case / Test Run Layout",
        general: true,

        component: TemplatesTab,
        requiresPermission: canViewTemplate,
      },
      {
        id: "fields",
        label: "Fields",
        general: true,

        component: FieldsTab,
        requiresPermission: canViewTemplate,
      },
    ]
    return currSection === "general" ? data?.filter((item) => item.general) : data;
  }, [currSection])

  // Filter sections by RBAC (chief-admin: all sections)
  const baseVisibleSections = isUserChiefAdmin
    ? sections
    : sections.filter((section) => section.requiresPermission);
  const visibleSections = isEditFromAllClients
    ? baseVisibleSections.filter((s) => s.id === "details")
    : baseVisibleSections;

  const selectedSection = currentTab || (
    isEditFromAllClients
      ? "details"
      : (() => {
        const sectionParam = searchParams.get("section");
        if (!sectionParam) return "details";
        if (isUserChiefAdmin) {
          return sections.some((s) => s.id === sectionParam)
            ? sectionParam
            : "details";
        }
        return visibleSections.some((s) => s.id === sectionParam)
          ? sectionParam
          : "details";
      })())

  console.log({ selectedSection, currentTab })

  // Store client_id in localStorage when it's provided via URL or when it changes (skip when editing from All Clients)
  useEffect(() => {
    if (isEditFromAllClients) return;
    if (urlClientId) {
      storeClientId(urlClientId);
    } else if (effectiveClientId && effectiveClientId !== user?.client_id) {
      storeClientId(effectiveClientId);
    }
  }, [urlClientId, effectiveClientId, user?.client_id, isEditFromAllClients]);

  // Keep URL on Client Details only when in "edit from All Clients" flow
  useEffect(() => {
    if (!isEditFromAllClients || !urlClientId) return;
    const section = searchParams.get("section");
    if (section && section !== "details") {
      const params = new URLSearchParams(searchParams);
      params.set("section", "details");
      params.set("client_id", urlClientId);
      params.set("from", "all-clients");
      navigate(`/settings?${params.toString()}`, { replace: true });
    }
  }, [isEditFromAllClients, urlClientId, searchParams, navigate]);

  const storageListener = ({ detail }) => {
    setIsSidebarClosed(detail.collapsed);
  };
  useEffect(() => {
    if (currSection === "general") {
      setCurrentTab(sections[0].id)
    }
    setIsSidebarClosed(
      document.getElementsByClassName("sidebar")?.[0].clientWidth < 100,
    );
    window.addEventListener("sidebarEvent", storageListener);
    return () => {
      window.removeEventListener("sidebarEvent", storageListener);
    };
  }, []);

  // Early returns must come after all hooks
  if (settingsAccessLoading && !isUserChiefAdmin) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2E9E6F]" />
      </div>
    );
  }

  if (!canAccessSettings) {
    return <Navigate to="/projects" replace />;
  }

  // For chief-admin without client_id, show warning (check role_name from roles table first)
  if (isUserChiefAdmin && !effectiveClientId) {
    return (
      <div className="space-y-6">
        <div className="card p-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">
              Client Selection Required
            </h2>
            <p className="text-gray-600 mb-4">
              As a Chief Admin, please select a client to manage settings. You
              can access settings from the client management page or add
              ?client_id=... to the URL.
            </p>
            <button
              onClick={() => navigate("/clients")}
              className="btn-primary"
            >
              Go to Client Management
            </button>
          </div>
        </div>
      </div>
    );
  }



  const handleTabChange = (value) => {
    console.log({ currSection, value })
    if (currSection === "general")
      return setCurrentTab(value)
    const params = new URLSearchParams(searchParams);
    params.set("section", value);
    if (effectiveClientId) {
      params.set("client_id", effectiveClientId);
    }
    if (isEditFromAllClients) {
      params.set("from", "all-clients");
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  if (!isUserChiefAdmin && visibleSections.length === 0) {
    return (
      <div className="card p-6 text-center text-text-secondary">
        <p className="text-base">
          You don&apos;t have permission to view any settings sections for this
          client. Contact an administrator if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      {/* <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
          <p className="text-text-secondary mt-1">Manage configuration</p>
        </div>
      </div> */}

      {/* Main Content with Tabs */}
      <div className="card">
        <Tabs
          value={selectedSection}
          onValueChange={handleTabChange}
          activationMode="manual"
          className="w-full"
        >
          {currSection === "general" && visibleSections.length > 1 && (
            <div className="border-b px-6 pt-6">
              <TabsList
                className="h-auto bg-transparent p-0 w-full justify-start gap-1"
                style={{ overflow: "scroll" }}
              >
                {visibleSections.map((section) => (
                  <TabsTrigger
                    key={section.id}
                    value={section.id}
                    className="data-[state=active]:border-b-2 data-[state=active]:border-[#2E9E6F] data-[state=active]:text-[#2E9E6F] rounded-none px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors border-b-2 border-transparent"
                  >
                    {section.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          )}
          <div className="p-6">
            {visibleSections.map((section) => {
              const Component = section.component;
              const isOpen = selectedSection === section.id;
              const editExitProps =
                isEditFromAllClients && section.id === "details"
                  ? { returnToAllClients: true }
                  : {};
              return (
                <TabsContent
                  key={section.id}
                  value={section.id}
                  className="mt-0"
                >
                  <Suspense
                    fallback={
                      <div className="flex min-h-[40vh] items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-[#2E9E6F]" />
                      </div>
                    }
                  >
                    <Component
                      clientId={effectiveClientId}
                      isOpen={isOpen}
                      {...(section.props || {})}
                      {...editExitProps}
                    />
                  </Suspense>
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
